import type { Glosario, TerminoAEvitar, TerminoGlosario } from '@/api/installation.api';

/**
 * EL GLOSARIO DEL NEGOCIO — lo que la pantalla necesita saber de él.
 *
 * No es documentación: cada fila viaja dentro del prompt del asistente interno.
 * Por eso una fila a medio llenar importa: se descartaba al guardar, en
 * silencio, y el dueño se quedaba creyendo que la había cargado. Acá vive lo
 * que permite avisarlo ANTES, y buscar cuando la lista ya no entra en pantalla.
 */

/** Texto comparable: sin mayúsculas, sin acentos y sin espacios de más. */
function comparable(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();
}

/** Qué le falta a una palabra para que se guarde. `null` = está completa. */
export function faltaEnTermino(t: TerminoGlosario): string | null {
  if (t.termino.trim() === '') return 'Falta la palabra: esta fila no se va a guardar.';
  if (t.significa.trim() === '') {
    return 'Falta qué significa acá adentro: esta fila no se va a guardar.';
  }
  return null;
}

/** Qué le falta a una regla para que se guarde. `null` = está completa. */
export function faltaEnRegla(e: TerminoAEvitar): string | null {
  if (e.no.trim() === '') return 'Falta la palabra que no hay que decir: esta fila no se guarda.';
  // Decir "no digas empleado" sin dar la alternativa no le sirve al asistente:
  // se queda sin nada con qué reemplazarla.
  if (e.si.trim() === '') return 'Falta con qué reemplazarla: esta fila no se va a guardar.';
  return null;
}

/** Las palabras que coinciden con lo buscado, por la palabra o por su significado. */
export function filtrarTerminos(terminos: TerminoGlosario[], busqueda: string): TerminoGlosario[] {
  const q = comparable(busqueda);
  if (q === '') return terminos;
  return terminos.filter(
    (t) => comparable(t.termino).includes(q) || comparable(t.significa).includes(q),
  );
}

/** Las reglas que coinciden con lo buscado, de cualquiera de los dos lados. */
export function filtrarEvitar(evitar: TerminoAEvitar[], busqueda: string): TerminoAEvitar[] {
  const q = comparable(busqueda);
  if (q === '') return evitar;
  return evitar.filter((e) => comparable(e.no).includes(q) || comparable(e.si).includes(q));
}

/** Cuántas hay de cada cosa y cuántas están a medio llenar. */
export function resumenDeGlosario(g: Glosario): {
  palabras: number;
  palabrasIncompletas: number;
  reglas: number;
  reglasIncompletas: number;
} {
  const terminos = g.terminos ?? [];
  const evitar = g.evitar ?? [];
  return {
    palabras: terminos.length,
    palabrasIncompletas: terminos.filter((t) => faltaEnTermino(t) !== null).length,
    reglas: evitar.length,
    reglasIncompletas: evitar.filter((e) => faltaEnRegla(e) !== null).length,
  };
}
